/**
 * EHR Extension Promo Slice
 *
 * Persisted Redux slice tracking promo popup dismissal state.
 *
 * - dismissed: true → permanently hidden (user clicked X or "Learn More")
 * - snoozedUntil: timestamp → hidden until that time (user clicked "Maybe later")
 */

import { createSlice } from '@reduxjs/toolkit';

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

export interface EhrExtensionPromoState {
  dismissed: boolean;
  snoozedUntil: number | null; // Unix timestamp (ms)
  homeBannerDismissed: boolean;
  homeBannerSnoozedUntil: number | null; // Unix timestamp (ms)
}

const initialState: EhrExtensionPromoState = {
  dismissed: false,
  snoozedUntil: null,
  homeBannerDismissed: false,
  homeBannerSnoozedUntil: null,
};

export const ehrExtensionPromoSlice = createSlice({
  name: 'ehrExtensionPromo',
  initialState,
  reducers: {
    /** Permanently dismiss the promo (X button or "Learn More") */
    dismissPromo: (state) => {
      state.dismissed = true;
      state.snoozedUntil = null;
    },

    /** Snooze the promo for 2 weeks ("Maybe later") */
    snoozePromo: (state) => {
      state.snoozedUntil = Date.now() + TWO_WEEKS_MS;
    },

    /** Permanently dismiss the home page promo banner (X button) */
    dismissHomeBanner: (state) => {
      state.homeBannerDismissed = true;
    },

    /** Snooze the home page promo banner for 2 weeks ("Set up" click) */
    snoozeHomeBanner: (state) => {
      state.homeBannerSnoozedUntil = Date.now() + TWO_WEEKS_MS;
    },

    /** Reset promo state (for testing/debugging) */
    resetPromo: () => initialState,
  },
});

export const {
  dismissPromo,
  snoozePromo,
  dismissHomeBanner,
  snoozeHomeBanner,
  resetPromo,
} = ehrExtensionPromoSlice.actions;

export default ehrExtensionPromoSlice.reducer;
